import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Plus, Target, CalendarClock, ChevronDown, ChevronRight, ClipboardList } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AppShell } from '@/components/app/AppShell';
import {
  CycleStatus, CycleType, ReviewKind, PerformanceReview,
  useCreateCycle, useCreateGoal, useCycles, useGoals,
  useSetCycleStatus, useUpdateGoal, useCycleReviews, useCreateReview, useSubmitReview,
} from '@/lib/performance';
import { useEmployees } from '@/lib/employees';

const CYCLE_TYPES: CycleType[] = ['quarterly', 'half_year', 'annual', 'adhoc'];

// Cycle lifecycle: draft → open → review → closed. Map each state to the
// single forward action surfaced on the row (closed is terminal).
const CYCLE_NEXT: Partial<Record<CycleStatus, { to: CycleStatus; label: string }>> = {
  draft:  { to: 'open',   label: 'Activate' },
  open:   { to: 'review', label: 'Start reviews' },
  review: { to: 'closed', label: 'Close cycle' },
};

const REVIEW_KINDS: ReviewKind[] = ['self', 'manager', 'peer', 'skip_level'];

const REVIEW_STATUS_COLOR: Record<string, string> = {
  draft:        'bg-slate-500/10 text-slate-700 dark:text-slate-300',
  submitted:    'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  acknowledged: 'bg-violet-500/10 text-violet-700 dark:text-violet-300',
};

const STATUS_COLOR: Record<string, string> = {
  active:   'bg-sky-500/10 text-sky-700 dark:text-sky-300',
  on_track: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  at_risk:  'bg-amber-500/10 text-amber-700 dark:text-amber-300',
  done:     'bg-violet-500/10 text-violet-700 dark:text-violet-300',
  dropped:  'bg-slate-500/10 text-slate-700 dark:text-slate-300',
};

export function PerformancePage() {
  const { t } = useTranslation();
  const cycles = useCycles();
  const goals = useGoals();
  const setCycleStatus = useSetCycleStatus();
  const employees = useEmployees({});
  const [tab, setTab] = useState<'cycles' | 'goals'>('cycles');
  const [showAddCycle, setShowAddCycle] = useState(false);
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [expandedCycleId, setExpandedCycleId] = useState<string | null>(null);

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 max-w-7xl mx-auto space-y-6">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t('performance.eyebrow')}</p>
            <h1 className="display serif-jp text-3xl md:text-4xl font-semibold tracking-tight">{t('performance.title')}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('performance.subtitle')}</p>
          </div>
          <button
            onClick={() => tab === 'cycles' ? setShowAddCycle(true) : setShowAddGoal(true)}
            className="gradient-primary inline-flex h-11 items-center gap-2 px-5 rounded-xl text-white text-sm font-semibold shadow-glow hover:shadow-glow-pink transition"
          >
            <Plus className="h-4 w-4" />
            {tab === 'cycles' ? t('performance.addCycle') : t('performance.addGoal')}
          </button>
        </header>

        <div className="inline-flex rounded-xl border border-border bg-card p-1">
          <button
            onClick={() => setTab('cycles')}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition ${tab === 'cycles' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <CalendarClock className="inline h-4 w-4 me-1" />
            {t('performance.tabs.cycles')}
          </button>
          <button
            onClick={() => setTab('goals')}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition ${tab === 'goals' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <Target className="inline h-4 w-4 me-1" />
            {t('performance.tabs.goals')}
          </button>
        </div>

        {tab === 'cycles' && (
          <div className="rounded-2xl border border-border bg-card shadow-soft">
            <div className="px-5 py-3 border-b border-border bg-muted/30">
              <h2 className="text-sm font-semibold">{t('performance.cycles')}</h2>
            </div>
            {(cycles.data ?? []).length === 0 ? (
              <p className="px-5 py-12 text-center text-sm text-muted-foreground">{t('performance.emptyCycles')}</p>
            ) : (
              <ul className="divide-y divide-border">
                {(cycles.data ?? []).map((c) => {
                  const isExpanded = expandedCycleId === c.id;
                  return (
                    <li key={c.id}>
                      <div className="px-5 py-3 flex items-center gap-4">
                        <button
                          onClick={() => setExpandedCycleId(isExpanded ? null : c.id)}
                          className="shrink-0 text-muted-foreground hover:text-foreground transition"
                          aria-label={t('performance.reviews.toggle', { defaultValue: 'Toggle reviews' })}
                        >
                          {isExpanded
                            ? <ChevronDown className="h-4 w-4" />
                            : <ChevronRight className="h-4 w-4" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate">{c.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {t(`performance.cycleType.${c.cycleType}`)} · {c.startsOn} → {c.endsOn}
                          </p>
                        </div>
                        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-md ${STATUS_COLOR[c.status] ?? 'bg-muted'}`}>
                          {t(`performance.cycleStatus.${c.status}`)}
                        </span>
                        {CYCLE_NEXT[c.status] && (
                          <button
                            onClick={() => setCycleStatus.mutate({ id: c.id, status: CYCLE_NEXT[c.status]!.to })}
                            disabled={setCycleStatus.isPending}
                            className="shrink-0 inline-flex h-8 items-center px-3 rounded-lg border border-border text-xs font-semibold hover:bg-muted disabled:opacity-50 transition"
                          >
                            {t(`performance.cycleAction.${c.status}`, { defaultValue: CYCLE_NEXT[c.status]!.label })}
                          </button>
                        )}
                      </div>
                      {isExpanded && (
                        <CycleReviewsPanel
                          cycleId={c.id}
                          employeeOptions={(employees.data ?? []).map((e) => ({ id: e.id, label: e.preferredName || e.legalNameLocal }))}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {tab === 'goals' && (
          <div className="rounded-2xl border border-border bg-card shadow-soft">
            <div className="px-5 py-3 border-b border-border bg-muted/30">
              <h2 className="text-sm font-semibold">{t('performance.goals')}</h2>
            </div>
            {(goals.data ?? []).length === 0 ? (
              <p className="px-5 py-12 text-center text-sm text-muted-foreground">{t('performance.emptyGoals')}</p>
            ) : (
              <ul className="divide-y divide-border">
                {(goals.data ?? []).map((g) => (
                  <GoalRow key={g.id} g={g} />
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {showAddCycle && (
        <AddCycleDialog onClose={() => setShowAddCycle(false)} />
      )}
      {showAddGoal && (
        <AddGoalDialog
          onClose={() => setShowAddGoal(false)}
          employeeOptions={(employees.data ?? []).map((e) => ({ id: e.id, label: e.preferredName || e.legalNameLocal }))}
          cycleOptions={(cycles.data ?? []).map((c) => ({ id: c.id, label: c.name }))}
        />
      )}
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Reviews panel — shown inline when a cycle row is expanded
// ---------------------------------------------------------------------------

function CycleReviewsPanel({
  cycleId,
  employeeOptions,
}: {
  cycleId: string;
  employeeOptions: Array<{ id: string; label: string }>;
}) {
  const { t } = useTranslation();
  const reviews = useCycleReviews(cycleId);
  const createReview = useCreateReview();
  const [showAdd, setShowAdd] = useState(false);
  const [submitTarget, setSubmitTarget] = useState<PerformanceReview | null>(null);

  // Add-review form state
  const [subjectEmployeeId, setSubjectEmployeeId] = useState(employeeOptions[0]?.id ?? '');
  const [kind, setKind] = useState<ReviewKind>('self');

  function handleAddReview(e: React.FormEvent) {
    e.preventDefault();
    if (!subjectEmployeeId) return;
    createReview.mutateAsync({ cycleId, subjectEmployeeId, kind }).then(() => {
      setShowAdd(false);
    });
  }

  return (
    <div className="mx-5 mb-4 rounded-xl border border-border bg-muted/20">
      <div className="px-4 py-2 flex items-center justify-between border-b border-border">
        <span className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          <ClipboardList className="h-3.5 w-3.5" />
          {t('performance.reviews.title', { defaultValue: 'Reviews' })}
        </span>
        <button
          onClick={() => setShowAdd((v) => !v)}
          className="inline-flex h-7 items-center gap-1 px-2.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted transition"
        >
          <Plus className="h-3.5 w-3.5" />
          {t('performance.reviews.add', { defaultValue: 'Add review' })}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={handleAddReview} className="px-4 py-3 border-b border-border flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[160px]">
            <label className="text-[10px] font-semibold text-muted-foreground uppercase">
              {t('performance.reviews.subject', { defaultValue: 'Subject employee' })}
            </label>
            <select
              value={subjectEmployeeId}
              onChange={(e) => setSubjectEmployeeId(e.target.value)}
              className="mt-0.5 w-full h-8 rounded-lg border border-border bg-card px-2 text-xs"
            >
              {employeeOptions.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[130px]">
            <label className="text-[10px] font-semibold text-muted-foreground uppercase">
              {t('performance.reviews.kind', { defaultValue: 'Kind' })}
            </label>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as ReviewKind)}
              className="mt-0.5 w-full h-8 rounded-lg border border-border bg-card px-2 text-xs"
            >
              {REVIEW_KINDS.map((k) => (
                <option key={k} value={k}>{t(`performance.reviewKind.${k}`, { defaultValue: k.replace('_', ' ') })}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setShowAdd(false)}
              className="inline-flex h-8 items-center px-3 rounded-lg border border-border text-xs font-semibold hover:bg-muted transition"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={createReview.isPending}
              className="gradient-primary inline-flex h-8 items-center px-3 rounded-lg text-white text-xs font-semibold shadow-glow disabled:opacity-60 transition"
            >
              {createReview.isPending ? t('common.saving') : t('common.create')}
            </button>
          </div>
        </form>
      )}

      {reviews.isLoading ? (
        <p className="px-4 py-3 text-xs text-muted-foreground">{t('common.loading', { defaultValue: 'Loading…' })}</p>
      ) : (reviews.data ?? []).length === 0 ? (
        <p className="px-4 py-3 text-xs text-muted-foreground text-center">
          {t('performance.reviews.empty', { defaultValue: 'No reviews yet. Add one above.' })}
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {(reviews.data ?? []).map((rev) => {
            const subjectLabel = employeeOptions.find((e) => e.id === rev.subjectEmployeeId)?.label ?? rev.subjectEmployeeId;
            return (
              <li key={rev.id} className="px-4 py-2.5 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate">
                    <Link
                      to={`/app/people/${rev.subjectEmployeeId}`}
                      className="hover:underline text-foreground"
                    >
                      {subjectLabel}
                    </Link>
                    <span className="ms-1.5 text-muted-foreground font-normal">
                      · {t(`performance.reviewKind.${rev.kind}`, { defaultValue: rev.kind.replace('_', ' ') })}
                    </span>
                  </p>
                  {rev.rating !== null && (
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {t('performance.reviews.rating', { defaultValue: 'Rating' })}: {rev.rating}/5
                      {rev.strengths ? ` · ${rev.strengths.slice(0, 40)}${rev.strengths.length > 40 ? '…' : ''}` : ''}
                    </p>
                  )}
                </div>
                <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-md ${REVIEW_STATUS_COLOR[rev.status] ?? 'bg-muted'}`}>
                  {t(`performance.reviewStatus.${rev.status}`, { defaultValue: rev.status })}
                </span>
                {rev.status === 'draft' && (
                  <button
                    onClick={() => setSubmitTarget(rev)}
                    className="shrink-0 inline-flex h-7 items-center px-2.5 rounded-lg border border-border text-[10px] font-semibold hover:bg-muted transition"
                  >
                    {t('performance.reviews.submit', { defaultValue: 'Submit' })}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {submitTarget && (
        <SubmitReviewDialog
          review={submitTarget}
          subjectLabel={employeeOptions.find((e) => e.id === submitTarget.subjectEmployeeId)?.label ?? submitTarget.subjectEmployeeId}
          onClose={() => setSubmitTarget(null)}
        />
      )}
    </div>
  );
}

function SubmitReviewDialog({
  review,
  subjectLabel,
  onClose,
}: {
  review: PerformanceReview;
  subjectLabel: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const submit = useSubmitReview();
  const [rating, setRating] = useState<number>(3);
  const [strengths, setStrengths] = useState('');
  const [growthAreas, setGrowthAreas] = useState('');
  const [comments, setComments] = useState('');

  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm grid place-items-center px-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit.mutateAsync({ id: review.id, rating, strengths: strengths || undefined, growthAreas: growthAreas || undefined, comments: comments || undefined }).then(onClose);
        }}
        className="w-full max-w-md rounded-2xl bg-white dark:bg-card shadow-glow border border-border p-6 space-y-3"
      >
        <h2 className="text-lg font-semibold">
          {t('performance.reviews.submitTitle', { defaultValue: 'Submit Review' })}
        </h2>
        <p className="text-xs text-muted-foreground">
          <Link to={`/app/people/${review.subjectEmployeeId}`} className="hover:underline font-semibold text-foreground">
            {subjectLabel}
          </Link>
          <span className="ms-1.5">
            · {t(`performance.reviewKind.${review.kind}`, { defaultValue: review.kind.replace('_', ' ') })}
          </span>
        </p>

        <div>
          <label className="text-xs font-semibold text-muted-foreground">
            {t('performance.reviews.ratingLabel', { defaultValue: 'Rating (1–5)' })}
            <span className="text-destructive ms-1">*</span>
          </label>
          <div className="mt-1 flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setRating(n)}
                className={`h-8 w-8 rounded-lg border text-sm font-semibold transition ${
                  rating === n
                    ? 'gradient-primary text-white border-transparent shadow-glow'
                    : 'border-border hover:bg-muted text-muted-foreground'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        <TextAreaField
          label={t('performance.reviews.strengths', { defaultValue: 'Strengths' })}
          value={strengths}
          onChange={setStrengths}
        />
        <TextAreaField
          label={t('performance.reviews.growthAreas', { defaultValue: 'Growth areas' })}
          value={growthAreas}
          onChange={setGrowthAreas}
        />
        <TextAreaField
          label={t('performance.reviews.comments', { defaultValue: 'Comments' })}
          value={comments}
          onChange={setComments}
        />

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 flex-1 items-center justify-center rounded-xl border border-border text-sm font-semibold hover:bg-muted"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={submit.isPending}
            className="gradient-primary inline-flex h-10 flex-1 items-center justify-center rounded-xl text-white text-sm font-semibold shadow-glow disabled:opacity-60"
          >
            {submit.isPending ? t('common.saving') : t('performance.reviews.submitBtn', { defaultValue: 'Submit review' })}
          </button>
        </div>
      </form>
    </div>
  );
}

function TextAreaField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="text-xs font-semibold text-muted-foreground">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        className="mt-1 w-full rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Goal row
// ---------------------------------------------------------------------------

function GoalRow({ g }: { g: { id: string; title: string; progress: number; status: string; dueOn: string | null; metric: string | null } }) {
  const update = useUpdateGoal();
  const { t } = useTranslation();
  return (
    <li className="px-5 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm font-semibold truncate">{g.title}</p>
        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-md ${STATUS_COLOR[g.status] ?? 'bg-muted'}`}>
          {t(`performance.goalStatus.${g.status}`)}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mt-0.5">
        {g.metric ? `${g.metric} · ` : ''}{g.dueOn ?? t('performance.noDueDate')}
      </p>
      <div className="mt-2 flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-indigo-500 to-fuchsia-500"
            initial={{ width: 0 }}
            animate={{ width: `${g.progress}%` }}
            transition={{ duration: 0.6 }}
          />
        </div>
        <span className="num text-xs font-semibold w-9 text-end">{g.progress}%</span>
        <input
          type="range" min={0} max={100} value={g.progress}
          onChange={(e) => update.mutate({ id: g.id, progress: Number(e.target.value) })}
          className="w-24 accent-primary"
        />
      </div>
    </li>
  );
}

function AddCycleDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const create = useCreateCycle();
  const [name, setName] = useState('');
  const [cycleType, setCycleType] = useState<CycleType>('half_year');
  const [startsOn, setStartsOn] = useState('');
  const [endsOn, setEndsOn] = useState('');

  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm grid place-items-center px-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name || !startsOn || !endsOn) return;
          create.mutateAsync({ name, cycleType, startsOn, endsOn }).then(onClose);
        }}
        className="w-full max-w-md rounded-2xl bg-white dark:bg-card shadow-glow border border-border p-6 space-y-3"
      >
        <h2 className="text-lg font-semibold">{t('performance.dialog.cycleTitle')}</h2>
        <Field label={t('performance.dialog.name')} value={name} onChange={setName} required />
        <div>
          <label className="text-xs font-semibold text-muted-foreground">{t('performance.dialog.type')}</label>
          <select
            value={cycleType}
            onChange={(e) => setCycleType(e.target.value as CycleType)}
            className="mt-1 w-full h-10 rounded-xl border border-border bg-card px-3 text-sm"
          >
            {CYCLE_TYPES.map((c) => <option key={c} value={c}>{t(`performance.cycleType.${c}`)}</option>)}
          </select>
        </div>
        <Field label={t('performance.dialog.starts')} value={startsOn} onChange={setStartsOn} type="date" required />
        <Field label={t('performance.dialog.ends')} value={endsOn} onChange={setEndsOn} type="date" required />
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="inline-flex h-10 flex-1 items-center justify-center rounded-xl border border-border text-sm font-semibold hover:bg-muted">{t('common.cancel')}</button>
          <button type="submit" disabled={create.isPending} className="gradient-primary inline-flex h-10 flex-1 items-center justify-center rounded-xl text-white text-sm font-semibold shadow-glow disabled:opacity-60">{create.isPending ? t('common.saving') : t('common.create')}</button>
        </div>
      </form>
    </div>
  );
}

function AddGoalDialog({
  onClose, employeeOptions, cycleOptions,
}: {
  onClose: () => void;
  employeeOptions: Array<{ id: string; label: string }>;
  cycleOptions: Array<{ id: string; label: string }>;
}) {
  const { t } = useTranslation();
  const create = useCreateGoal();
  const [employeeId, setEmployeeId] = useState(employeeOptions[0]?.id ?? '');
  const [cycleId, setCycleId] = useState('');
  const [title, setTitle] = useState('');
  const [metric, setMetric] = useState('');
  const [dueOn, setDueOn] = useState('');

  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm grid place-items-center px-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!employeeId || !title) return;
          create.mutateAsync({ employeeId, cycleId: cycleId || undefined, title, metric: metric || undefined, dueOn: dueOn || undefined }).then(onClose);
        }}
        className="w-full max-w-md rounded-2xl bg-white dark:bg-card shadow-glow border border-border p-6 space-y-3"
      >
        <h2 className="text-lg font-semibold">{t('performance.dialog.goalTitle')}</h2>
        <div>
          <label className="text-xs font-semibold text-muted-foreground">{t('performance.dialog.employee')}</label>
          <select
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            className="mt-1 w-full h-10 rounded-xl border border-border bg-card px-3 text-sm"
          >
            {employeeOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground">
            {t('performance.dialog.cycle', { defaultValue: 'Review cycle' })}
            <span className="text-muted-foreground ms-2 font-normal">{t('common.optional', { defaultValue: 'optional' })}</span>
          </label>
          <select
            value={cycleId}
            onChange={(e) => setCycleId(e.target.value)}
            className="mt-1 w-full h-10 rounded-xl border border-border bg-card px-3 text-sm"
          >
            <option value="">{t('performance.dialog.noCycle', { defaultValue: 'No cycle' })}</option>
            {cycleOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </div>
        <Field label={t('performance.dialog.title')} value={title} onChange={setTitle} required />
        <Field label={t('performance.dialog.metric')} value={metric} onChange={setMetric} />
        <Field label={t('performance.dialog.dueOn')} value={dueOn} onChange={setDueOn} type="date" />
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="inline-flex h-10 flex-1 items-center justify-center rounded-xl border border-border text-sm font-semibold hover:bg-muted">{t('common.cancel')}</button>
          <button type="submit" disabled={create.isPending} className="gradient-primary inline-flex h-10 flex-1 items-center justify-center rounded-xl text-white text-sm font-semibold shadow-glow disabled:opacity-60">{create.isPending ? t('common.saving') : t('common.create')}</button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label, value, onChange, type = 'text', required,
}: { label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean }) {
  return (
    <div>
      <label className="text-xs font-semibold text-muted-foreground">
        {label}
        {required && <span className="text-destructive ms-1">*</span>}
      </label>
      <input
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full h-10 rounded-xl border border-border bg-card px-3 text-sm outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
      />
    </div>
  );
}
